import { useEffect, useState } from 'react';
import { useGameStore } from '@/ui/store/gameStore';
import { stallApi, ApiError } from '@/net/api';

/**
 * Ile sztuk każdego rodzaju jajka jest WOLNYCH — czyli bez tych wystawionych
 * na sprzedaż w budce (E-6 FIXES, Codex LOW 4).
 *
 * DLACZEGO OSOBNY HOOK: pełny zapis gry zwraca WSZYSTKIE jajka gracza, bez
 * informacji, które są zarezerwowane pod ofertę (`in_stall_id` nie jest częścią
 * `PlayerSave` — patrz inwariant I-3). Każdy ekran, który proponuje coś zrobić
 * z jajkami, musi więc sam odjąć wystawione sztuki, inaczej zaoferuje akcję,
 * której serwer nie wykona.
 *
 * Ten sam rachunek robił wcześniej ekwipunek jajek; wymiana 3→1 potrzebowała go
 * drugi raz, a dwie kopie tej samej arytmetyki rozjechałyby się przy pierwszej
 * zmianie reguł.
 *
 * `failed` NIE jest ukrywane: gdy nie wiemy, co jest wystawione, ekran ma o tym
 * powiedzieć i pozwolić ponowić — ciche założenie „wszystko wolne" prowadzi
 * gracza prosto w odmowę serwera.
 */
export interface FreeEggCounts {
  /** Rodzaj → liczba sztuk NIEwystawionych. */
  free: Record<string, number>;
  /** Rodzaj → liczba sztuk wystawionych w budce. */
  listed: Record<string, number>;
  /** Czy pobranie budek się nie udało (gość NIE jest błędem). */
  failed: boolean;
  /** Ponowne pobranie — do podpięcia pod przycisk „Spróbuj ponownie". */
  reload: () => void;
}

export function useFreeEggCounts(): FreeEggCounts {
  const eggInventory = useGameStore((s) => s.eggInventory);
  const [listed, setListed] = useState<Record<string, number>>({});
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await stallApi.mine();
        if (cancelled) return;
        const counts: Record<string, number> = {};
        for (const stall of res.stalls) {
          for (const l of stall.egg_listings ?? []) {
            counts[l.egg_def_id] = (counts[l.egg_def_id] ?? 0) + l.quantity;
          }
        }
        setListed(counts);
        setFailed(false);
      } catch (err) {
        if (cancelled) return;
        // Gość nie ma budek i nigdy nie będzie ich miał — dla niego pusty wynik
        // jest poprawną odpowiedzią, nie awarią.
        const isGuest = err instanceof ApiError && err.code === 'guest_cannot_trade';
        setListed({});
        setFailed(!isGuest);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const owned: Record<string, number> = {};
  for (const egg of eggInventory) owned[egg.defId] = (owned[egg.defId] ?? 0) + 1;

  const free: Record<string, number> = {};
  for (const [defId, count] of Object.entries(owned)) {
    free[defId] = Math.max(0, count - (listed[defId] ?? 0));
  }

  return { free, listed, failed, reload: () => setNonce((n) => n + 1) };
}
